import { FormHTMLAttributes, useState } from 'react'
import { useFormik } from 'formik'
import parse from 'html-react-parser'

import Button from '@/components/base/Button'
import FormControl from '@/components/base/FormControl'
import Spinner from '@/components/base/Spinner'
import Typography from '@/components/base/Typography'
import {
  convertJsontoFormData,
  createInitialValues,
  createYupSchema
} from '@/utils/form'
import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import { cn } from '@/lib/utils'
import { WPGravityForm } from '@/types'

export type FormProps = FormHTMLAttributes<HTMLFormElement> &
  Partial<{
    form_id: number
    submit: string
    fields: WPGravityForm['form']
    theme?: 'dark' | 'light'
  }>

const Form = ({
  form_id,
  fields = [],
  submit,
  className,
  theme = 'light',
  ...props
}: FormProps) => {
  const [statusMessage, setStatusMessage] = useState('&nbsp;')

  async function sendContactForm(sendData: any) {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const response = await axios.post(
      `${API.GRAVITY_FORM}/${form_id}/submissions`,
      convertJsontoFormData(sendData),
      { headers }
    )
    return response
  }

  const formik = useFormik<any>({
    initialValues: createInitialValues(fields) || {},
    validationSchema: createYupSchema(fields, 'it'),
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting, resetForm }) => {
      const form_url_id = fields.find((field) => field.label === 'page_url')?.id
      const result = sendContactForm({
        ...values,
        ...(form_url_id
          ? { [`input_${form_url_id}`]: window.location.href }
          : {})
      })
      result
        .then((response) => {
          setStatusMessage(response.data.confirmation_message)
          setStatus(response.data.is_valid ? 'success' : 'error')
        })
        .then(() => {
          setTimeout(() => {
            resetForm()
            setStatusMessage('&nbsp;')
            setStatus('')

            setSubmitting(false)
          }, 3000)
        })
    }
  })

  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
      className={cn(['grid gap-gutter md:grid-cols-2', className])}
      {...props}
    >
      {fields?.map((field, index) => (
        <FormControl
          key={index}
          theme={theme}
          id={`input_${field.id}`}
          name={`input_${field.id}`}
          type={field.type}
          label={field.type === 'consent' ? field.checkboxLabel : field.label}
          required={field.isRequired}
          options={field.choices}
          placeholder={`${field.placeholder}${field.isRequired ? ' *' : ''}`}
          checked={formik.values[`input_${field.id}`] || false}
          value={formik.values[`input_${field.id}`]}
          disabled={formik.isSubmitting}
          onChange={(e) => {
            formik.handleChange(e)
            if (field.type === 'consent') {
              formik.setFieldValue(
                `input_${field.id}_1`,
                e.target.checked ? '1' : '0',
                false
              )
            }
            if (field.type === 'select') {
              formik.setFieldValue(`input_${field.id}`, e, false)
            }
          }}
          onBlur={formik.handleBlur}
          error={
            formik.errors[`input_${field.id}`] !== undefined &&
            formik.touched[`input_${field.id}`] !== undefined
          }
        />
      ))}

      <div className="mt-0 flex justify-center md:col-span-2 md:mt-[1.5rem]">
        <Button
          className="flex items-center gap-4"
          type="submit"
          disabled={
            formik.isSubmitting ||
            (Object.values(formik.errors).length > 0 && formik.submitCount > 0)
          }
        >
          <span data-label={submit}>
            <Typography variant="label">{submit}</Typography>
          </span>
          {formik.isSubmitting ? (
            <Spinner theme="dark" variant="small" />
          ) : null}
        </Button>
      </div>

      {statusMessage !== '' ? (
        <div
          className={cn([
            'w-full select-none text-center text-body md:col-span-2',
            {
              'text-dark-500': formik.status !== 'error' && theme === 'light',
              'text-white': formik.status !== 'error' && theme === 'dark',
              'text-red-500': formik.status === 'error'
            }
          ])}
        >
          {parse(statusMessage)}
        </div>
      ) : null}
    </form>
  )
}

export default Form
